import React, { useState } from 'react';
import Dropdown from './ui/Dropdown';
import FiltersMasterDetail, {
  DEFAULT_COUNT_LABEL,
  HIDDEN_COUNT_LABEL,
} from './ui/FiltersMasterDetail';
import OrderFilterGroupBody from './OrderFilterGroupBody';
import {
  TOOLBAR_DISCLOSURE_TRIGGER,
  TOOLBAR_GROUP,
  TOOLBAR_SEGMENT,
  TOOLBAR_SEGMENT_OFF,
  TOOLBAR_SEGMENT_ON,
  TOOLBAR_SEGMENT_TRACK,
} from './ui/Toolbar';
/* 🪤 `STATUS_LABELS` is DEFINED here, in `constants/colors.js`, and merely
   RE-EXPORTED by `constants/statuses.js` — import it from the definition, as
   `StatusBadge.js` already does. CLAUDE.md's shared-module bullet attributed it
   to `statuses.js`; that was imprecise and is corrected there. */
import { STATUS_LABELS } from '../constants/colors';

/**
 * THE order-list filter control: the All / Actionable view toggle, plus every
 * status pill behind a `Filters ▾` popover. Used by `DepartmentOrders.js`;
 * `Approvals.js` deliberately has neither (see below).
 * 🔻 **`MyOrders.js` LEFT IT ON 2026-09-30** for `ui/Tabs` (owner ruling), and
 * Department Orders passes `hideViewToggle` + `hideStatusGroup` — so the view
 * toggle and the status group have NO live caller today. Kept, not deleted:
 * removing them is its own change. Every "`MyOrders`" below is history.
 *
 * Product owner, 2026-08-08: *"most if not all where possible filters and pills
 * should be in a filter drop down like on the breakdown page."*
 *
 * ── WHAT IS IN THE POPOVER AND WHAT IS NOT, AND WHY ──
 * - **Status pills → IN.** They are a filter, they are the bulk of the row, and
 *   they are the thing the ruling names.
 * - **All / Actionable → OUT, on the toolbar.** It is a radio-style *view*
 *   toggle, not a filter — CLAUDE.md's *Filter Bar* section calls it exactly
 *   that — and it is the analogue of the breakdown's Table/Timeline/Labour view
 *   switcher, which also stays on the toolbar. Since 2026-08-08 it is drawn as
 *   ONE segmented control rather than two detached buttons; see `VIEW_GROUP`
 *   below for why that is a semantic fix and not only a cosmetic one.
 * - **Layout toggle and page size → OUT, and NOT this component's business.**
 *   They come in through `children`, never as internal JSX. If they were
 *   internal, `MyOrders` would acquire a Cards⇄Table toggle for free, and the
 *   sanctioned-exception box under UI Conventions → Order List Pages says it
 *   was left off that page ON PURPOSE ("nothing about it fell out for free").
 *   A shared component that silently grants a page a feature it was excluded
 *   from is how a sanctioned exception stops being sanctioned.
 *
 * ── 🔻 THE STRUCK-THROUGH PILL IS GONE (2026-08-10) — IT IS A CHECKBOX LIST,
 *    AND THE RULE THAT FORBADE THIS HAD ITS PREMISE EXPIRE, NOT ITS LOGIC ──
 * Product owner, offered both: *"yes do both and check boxes"*.
 *
 * This docblock, and `CLAUDE.md`, used to say: *"the inactive pill KEEPS its
 * strikethrough — `bg-gray-100 text-gray-400 line-through` means 'this status is
 * EXCLUDED from a set that defaults to ALL-ON'; the breakdown's inactive pill is
 * plain because `DEFAULT_FILTERS.statuses` starts EMPTY there, so copying its
 * flat inactive style inverts the reading of the whole control."*
 *
 * 🔑 **THAT WAS RIGHT, AND ITS PRECONDITION WAS THAT THE PILL IS THE ON/OFF
 * CARRIER.** It stops applying the moment a checkbox sits in front of the pill:
 * state is carried by the tick, the pill is a label, and `STATUS_COLORS` is
 * untouched. **This is NOT the flat inactive style the old rule forbade** — that
 * style deleted the state signal, this one moves it into a stronger channel. A
 * strike-through is a convention you must LEARN; a tick is one you READ, and
 * "ticked = shown" is the correct reading under an all-on default. The controls
 * live in `components/OrderFilterGroupBody.js`, which carries the full argument.
 *
 * ⚠️ **The mirror-image MODEL is completely unchanged and is still the trap** —
 * see `describeOrderFilters` and `orderFilterGroupCount` below. Only the drawing
 * of one control changed.
 *
 * ── 🔴 THE "ALL" BUTTON'S ACTIVE CONDITION IS COMPOUND, DELIBERATELY ──
 * `filterMode === 'all' && selectedStatuses.length === statuses.length`. "All"
 * un-highlights the moment you deselect a status, even though `filterMode` is
 * still `'all'` — because the list on screen is then NOT all of them. Clicking
 * it re-selects every status as well as setting the mode; that is the only
 * "select all statuses" affordance, which is why it must stay on the toolbar
 * and not follow the pills into the popover. Preserved verbatim from
 * `DepartmentOrders.js` / `MyOrders.js`.
 *
 * ── 🟡 "ACTIONABLE" MEANS THREE DIFFERENT THINGS AND MUST NOT BE UNIFIED ──
 * This component renders the toggle; it never decides what it means. Each page
 * owns its own predicate, and they are legitimately different:
 *   - `DepartmentOrders` — the SERVER's `can_action` flag, which is
 *     capability-dependent (see CLAUDE.md → Actionable Definition (per
 *     capability, never per role)).
 *   - `MyOrders` — `o.status === 'rejected'`, i.e. *your* order bounced back and
 *     needs resubmitting. Nothing to do with an approval chain.
 *   - `Approvals` — no such control at all: every row is `submitted` and waiting
 *     on the viewer, so the toggle would be a no-op.
 * Never push a shared `isActionable` into this file.
 *
 * ── 🟡 INTERSECTION SEMANTICS ARE THE CALLER'S AND ARE UNCHANGED ──
 * All/Actionable is mutually exclusive; the status pills are a secondary
 * multi-select that INTERSECTS with it (Actionable + Approved = approved orders
 * needing action). This component lifts state and renders; it filters nothing.
 * Moving the pills into a popover changed no page's filtering behaviour.
 *
 * ── ♿/⌨️ ──
 * The popover is `components/ui/Dropdown`, which already owns outside-click and
 * Escape close WITH focus returned to the trigger, `aria-expanded`,
 * `aria-haspopup="dialog"` + `role="dialog"`, and the rotating chevron. Do not
 * re-implement any of it here. 🪤 A pill inside a popover is NOT IN THE DOM
 * while the popover is closed — any test that queries a pill must open the
 * popover first.
 *
 * Props:
 *  - filterMode: 'all' | 'actionable'; onFilterModeChange(mode)
 *  - statuses: string[] — the pill order for this page
 *  - selectedStatuses: string[]; onToggleStatus(status)
 *  - onSelectAllStatuses(): re-select every status (fired by "All")
 *  - allCount / actionableCount: the two counts in the toggle labels
 *  - statusCounts: optional { [status]: number } — per-pill counts. My Orders
 *    showed these in its mobile sheet; omit for no counts.
 *  - testIdPrefix: `${prefix}-all`, `-actionable`, `-<status>`. 🚨 A CONTRACT —
 *    `e2e/orders.spec.js`, `e2e/online-orders-create-ui.spec.js` and
 *    `e2e/online-approvals-pending-visibility-ui.spec.js` select on
 *    `filter-all` / `filter-actionable` / `filter-submitted` (My Orders) and
 *    `dept-filter-all` / `dept-filter-actionable` (Department Orders). Do not
 *    rename them.
 *  - hideStatusGroup: optional, default false — drops the Status group from the
 *    popover for a page whose status filter is a TAB STRIP (`ui/Tabs`).
 *    Department Orders only. The default render is byte-identical (§9.1).
 *  - wrapperTestId: optional testid on the row (`status-filter` on My Orders,
 *    also an e2e contract).
 *  - children: the page's own controls — layout toggle, page size. A SLOT.
 */

/* 🗑️ `PILL_BASE` and `PILL_INACTIVE` were DELETED on 2026-08-10 with the
   checkbox conversion — the pill row they styled no longer exists, and the
   status pill inside the checkbox list is a LABEL whose classes live in
   `components/OrderFilterGroupBody.js`. Recorded rather than silently removed
   because `PILL_INACTIVE` carried the `line-through` this file's docblock argued
   for at length: **that argument is not lost, it is answered** — see the
   superseded-premise block above before re-adding either constant. */

/**
 * ── ALL | ACTIONABLE IS A SEGMENTED CONTROL, NOT TWO BUTTONS (2026-08-08) ──
 *
 * It is a RADIO — mutually exclusive, exactly one active at a time — and it was
 * drawn as two separate `border-2` buttons with a gap between them, which both
 * looked ragged and misstated the semantics: two detached buttons read as two
 * independent toggles that happen to sit together.
 *
 * 🔑 **THE SHAPE COMES FROM THE BREAKDOWN, BY PRODUCT RULING.** Owner,
 * 2026-08-08: *"breakdown is probably the best looking menu structure but I
 * still want a unified structure across the site."* So this takes
 * `TOOLBAR_SEGMENT_TRACK` — a tinted `p-1` recess with pill segments floating in
 * it, the `Table | Timeline | Labour` switcher's shape — rather than the
 * shared-border-and-divider shape the (since retired) Cards | Table toggle used. **Both order
 * pairs moved to it, so the two rhyme with the Breakdown and with each other.**
 *
 * 🟢 The ACTIVE-STATE COLOURS are the one thing that stays per-control, and
 * deliberately: "All" is blue and "Actionable" is green, which is the same green
 * `constants/rowStyles.js` uses for *"yours to action"* everywhere else on these
 * pages. Unifying them onto one hue would delete a real signal to make a control
 * tidier.
 */
const VIEW_SEG_ALL_ON = TOOLBAR_SEGMENT_ON;

const VIEW_SEG_ACTIONABLE_ON =
  'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';

/* 🗑️ `SECTION_LABEL_CLS` went with the pill row: the panel's one "STATUS"
   heading is now a rail TAB, which carries its own styling. */

/**
 * The `Filters ▾` trigger style now lives in `ui/Toolbar.js` as
 * `TOOLBAR_DISCLOSURE_TRIGGER`, because `Sort ▾` (`OrderSearchAndSort.js`)
 * became a second disclosure on the same bar on 2026-08-08 and the two must not
 * be allowed to drift apart. It was `FILTERS_TRIGGER_CLS`, a file-local const,
 * while this was the only caller. **Do not re-inline it here** — read the
 * docblock on the export before changing the string.
 */

/**
 * The active-filter chips for an order list, for `components/ui/FilterChips`.
 *
 * 🔑 A HIDDEN status is the chip, not a shown one — and that is forced by the
 * chip contract that `✕` must RELAX the filter. The order status multi-select
 * defaults to ALL-ON, so what narrows the list is an EXCLUSION; a chip per
 * *selected* status would give a ✕ that removes a status from view, i.e. makes
 * the list shorter, which is the opposite of what a chip's ✕ does everywhere
 * else in the app. It also matches the strikethrough pill the user just
 * clicked. (The breakdown chips its SELECTED statuses because its model is the
 * mirror image — default-empty, where a selection is the narrowing.)
 *
 * Search is not a chip: it has its own always-visible toolbar input.
 *
 * Returns `[{ key, label, patch }]`; `patch` is the partial filter state that
 * undoes that one chip.
 */
export function describeOrderFilters({
  filterMode,
  statuses,
  selectedStatuses,
  /* 🔀 Added 2026-08-10 with the Production group. BOTH default, so the two
     callers that do not pass them are byte-identical and `MyOrders.js` — which
     has no production filter at all — needs no change. */
  productionFilter = 'all',
  productionOptions = [],
  /* 🔀 Order Completion (2026-08-16) — a boolean `showCompleted`, REDESIGNED
     the same day from a THREE-STATE `completionFilter` that was withdrawn
     within hours (the `'only'` state is what let unticking a completed
     order's items make its own row vanish from the list — see
     `constants/statuses.js`). Default `true`, matching the withdrawn
     control's default-SHOW — the callers that omit it are byte-identical,
     the same additive shape `productionFilter` used. Unticked chips the
     EXCLUSION (`Completed hidden`), matching the order-status mirror-image
     rule; its ✕ RELAXES back to `true`, never the Breakdown's opposite
     convention. There is no second completion chip any more — "only
     completed" had no equivalent state to chip in the first place. */
  showCompleted = true,
}) {
  const chips = [];
  if (filterMode === 'actionable') {
    chips.push({ key: 'mode', label: 'Actionable only', patch: { filterMode: 'all' } });
  }
  if (!showCompleted) {
    chips.push({ key: 'completed', label: 'Completed hidden', patch: { showCompleted: true } });
  }
  statuses.forEach((s) => {
    if (selectedStatuses.includes(s)) return;
    chips.push({
      key: `status:${s}`,
      label: `Hidden: ${STATUS_LABELS[s] || s}`,
      patch: { selectedStatuses: [...selectedStatuses, s] },
    });
  });
  /* 🔑 THE PRODUCTION CHIP IS THE OTHER WAY ROUND FROM THE STATUS ONES, AND
     BOTH ARE CORRECT — this is the mirror-image rule applied per GROUP rather
     than per surface. A status chip names an EXCLUSION because the set defaults
     all-on; a production chip names the SELECTION because the filter defaults to
     "all productions". Each `✕` still RELAXES: unhiding a status widens the
     list, and dropping back to all productions widens it too. Reading the rule
     as "the order pages chip exclusions" and inverting this one would produce a
     ✕ that narrows.
     🪤 `String()` on both sides — `productionFilter` holds a `production_id` AS A
     STRING while the option's `id` is a NUMBER, so a bare `===` never matches and
     the chip silently reads "Production: <id>" for every real production. */
  if (productionFilter && productionFilter !== 'all') {
    const match = productionOptions.find((p) => String(p.id) === String(productionFilter));
    chips.push({
      key: 'production',
      label: `Production: ${match ? match.name : productionFilter}`,
      patch: { productionFilter: 'all' },
    });
  }
  return chips;
}

/**
 * How many OPTIONS are "on" inside one order filter group, for the rail in
 * `ui/FiltersMasterDetail`.
 *
 * 🚨 **THIS IS NOT `filterGroupCount` FROM `constants/gagBreakdown.js` AND THE
 * TWO DELIBERATELY DISAGREE — DO NOT IMPORT THAT ONE HERE, AND DO NOT UNIFY
 * THEM.** They answer the same question about mirror-image models:
 *   - The Breakdown's statuses default **EMPTY**, so a SELECTION narrows and its
 *     count is `filters.statuses.length`.
 *   - Order statuses default **ALL-ON**, so an EXCLUSION narrows and this count
 *     is the number HIDDEN.
 * Wiring the order rail to the Breakdown's function makes a resting panel
 * announce **"7 selected"** on a group that is filtering nothing — the exact
 * defect `constants/gagBreakdown.js` already records for the old owner scope,
 * where a value that is always "on" was counted and announced a filter that did
 * not exist.
 *
 * 🔑 **`status` returns exactly `hiddenCount`, the number already behind the
 * `Filters ▾ (N)` badge below.** One number, computed once, so the trigger and
 * the rail can never disagree — the property `GagFiltersMasterDetail` gets from
 * sharing `activeFilterCount` with its own trigger.
 *
 * 🪤 **`production` counts the SELECTION, not an exclusion**, because that filter
 * defaults to "all". Two groups on one surface counting in opposite directions
 * is why `ui/FiltersMasterDetail` takes `countLabel(n, key)` with the group key.
 */
export function orderFilterGroupCount({ statuses = [], selectedStatuses = [], productionFilter = 'all' }, key) {
  switch (key) {
    case 'status': return statuses.length - selectedStatuses.length;
    case 'production': return productionFilter && productionFilter !== 'all' ? 1 : 0;
    default: return 0;
  }
}

/**
 * How each group's count READS on the rail. See `orderFilterGroupCount` — the
 * two groups count in opposite directions, so one label would be wrong on one of
 * them. `HIDDEN_COUNT_LABEL` / `DEFAULT_COUNT_LABEL` are the shared readings;
 * the leading space in each is load-bearing for the accessible name.
 */
export function orderFilterCountLabel(n, key) {
  return key === 'status' ? HIDDEN_COUNT_LABEL(n) : DEFAULT_COUNT_LABEL(n);
}

/**
 * Apply a chip's `patch` (or the "Clear all" patch) to a page's filter state.
 * Shared so the two order pages do not each grow their own copy of the same
 * four lines — `npm run check-drift` would be right to complain.
 *
 * `hasOwnProperty` rather than truthiness: a patch legitimately carries
 * `filterMode: 'all'` and an empty-ish array, and "is this key present" is the
 * question being asked.
 */
export function applyOrderFilterPatch(patch, { setFilterMode, setSelectedStatuses, setProductionFilter, setShowCompleted }) {
  if (Object.prototype.hasOwnProperty.call(patch, 'filterMode')) setFilterMode(patch.filterMode);
  if (Object.prototype.hasOwnProperty.call(patch, 'selectedStatuses')) {
    setSelectedStatuses(patch.selectedStatuses);
  }
  /* 🪤 The setter is OPTIONAL and the guard tests BOTH. `MyOrders.js` passes no
     `setProductionFilter` because it has no such filter, so a patch key with no
     setter must be a no-op rather than a crash — and a setter with no key must
     not be called with `undefined`, which would silently clear a live filter. */
  if (Object.prototype.hasOwnProperty.call(patch, 'productionFilter') && setProductionFilter) {
    setProductionFilter(patch.productionFilter);
  }
  // Order Completion (2026-08-16) — same optional-setter shape as
  // `productionFilter` above; both order pages that carry this filter pass
  // `setShowCompleted`, no other caller needs to. Back to a plain boolean
  // setter after the same-day three-state detour (`completionFilter`) was
  // withdrawn — see `constants/statuses.js`.
  if (Object.prototype.hasOwnProperty.call(patch, 'showCompleted') && setShowCompleted) {
    setShowCompleted(patch.showCompleted);
  }
}

export default function OrderFilterBar({
  filterMode,
  onFilterModeChange,
  statuses,
  selectedStatuses,
  onToggleStatus,
  onSelectAllStatuses,
  allCount,
  actionableCount,
  statusCounts,
  /* 🔀 The Production group (2026-08-10). All three OPTIONAL and all three
     default, so a caller that passes none gets SINGLE-GROUP MODE — the panel
     `MyOrders.js` has always had, with no rail. The page decides whether to
     offer the group at all (it is admin-gated on Department Orders); this
     component never re-derives that. */
  productionOptions = [],
  productionFilter = 'all',
  onProductionFilterChange,
  /* 🔀 Order Completion (2026-08-16) — a boolean, back from the same-day
     three-state detour (see `constants/statuses.js`) — OPTIONAL, same
     additive shape as the production props. `Approvals.js` passes neither,
     so it is unaffected; `DepartmentOrders.js` and `MyOrders.js` both pass
     both. */
  showCompleted = true,
  onShowCompletedChange,
  /* 🔀 THE TABBED PAGE'S OPT-OUT (2026-09-12). Owner, of Department Orders:
     *"tabs replace the status checkboxes on this page"* — on THAT page only.
     Set it and the Status group leaves the popover, the badge stops counting
     hidden statuses, and the `All` segment stops asking about a multi-select
     that is no longer on this bar.
     🔑 **DEFAULT `false`, so every existing caller is BYTE-IDENTICAL** — pinned
     as literal HTML equality by §9.1 of `OrderFilterBar.test.js`, not asserted.
     `MyOrders.js` and `Approvals.js` pass nothing and are untouched.
     🚨 **This hides a CONTROL, never a filter.** The page that sets it owes the
     user an equivalent way to narrow by status — on Department Orders that is
     `ui/Tabs`. A caller that sets this and offers nothing has silently deleted
     the status filter from its page. */
  hideStatusGroup = false,
  /* 🔄 THE SECOND OPT-OUT (2026-09-12), and it removes the All / Actionable
     segmented control outright. Owner, of Department Orders: *"Actionable is not
     necessarily my task — a supervisor with a buyer does not want to see it for
     To order"*.
     🔑 **THE ARGUMENT, because it decides every future page that asks for this.**
     "Actionable" answers *could you act*, not *is this yours*. For a supervisor
     who holds `canManageDepartmentOrders` it therefore includes every `approved`
     order the BUYER is meant to place — so a personal reading of the label
     ("Actionable", "My tasks") is FALSE for any supervisor who delegates buying.
     The queue tabs already give a buyer `To order` / `To receive` and a
     supervisor `To approve`; the viewer's OWN steps live on My Approvals and are
     marked by the green chevron, which is untouched and still says "yours".
     🔻 **IT SUPERSEDES A STANDING RULE, SCOPED TO ONE PAGE.**
     `client/src/pages/CLAUDE.md` carries *"All / Actionable were NOT folded into
     a dropdown … Do not revisit."* That ruling forbids BURYING the control and
     still stands; this one REMOVES it from a page where the question is not worth
     asking. 🔴 **`MyOrders.js` keeps both the control and the old ruling** — its
     "Actionable" is `status === 'rejected'`, *your own order bounced back*, which
     IS true of the viewer personally.
     🔁 A "mine only" switch may return ONLY if a per-department "buying is
     delegated" preference is ever built — the owner's own condition. Do not
     reinstate this control to answer that; it is a different predicate.
     🔑 **DEFAULT `false`, so every existing caller is BYTE-IDENTICAL**, pinned as
     literal HTML equality by §10.1 rather than asserted. */
  hideViewToggle = false,
  testIdPrefix = 'filter',
  wrapperTestId,
  children,
}) {
  /* 🔴 Compound on purpose — see the docblock. "All" is not just the mode.
     ⚠️ **The compound half is ABOUT THE STATUS MULTI-SELECT, so it drops out
     with the control.** With the group hidden the page passes no
     `selectedStatuses` (or an empty one), and `0 === statuses.length` is false
     for ever — "All" would be permanently un-highlighted on a page where it is
     the resting state. The rule has not changed; its subject has left the bar. */
  const allActive = hideStatusGroup
    ? filterMode === 'all'
    : (filterMode === 'all' && selectedStatuses.length === statuses.length);
  // The popover badge counts HIDDEN statuses. All/Actionable is not counted: it
  // lives on the toolbar, where its own state is already visible.
  // 🔑 The rail's Status count is THIS SAME NUMBER, via `orderFilterGroupCount`,
  // so the trigger and the rail can never disagree.
  // 🪤 ZERO when the group is hidden, and this is not cosmetic: the expression
  // would otherwise read `7 - 0` on the tabbed page and announce seven hidden
  // statuses behind a popover that contains no status control.
  const hiddenCount = hideStatusGroup ? 0 : statuses.length - selectedStatuses.length;

  /* 🔑 THE PRODUCTION GROUP IS OFFERED ONLY WHEN IT WOULD DO SOMETHING — it needs
     both a handler and at least one option. A rail tab over an empty radio list
     is a category that cannot filter, which the master–detail docblock says does
     not belong on the rail. This is also what keeps `MyOrders.js` in single-group
     mode without that page passing anything. */
  const hasProductionGroup = Boolean(onProductionFilterChange) && productionOptions.length > 0;

  const groups = [
    ...(hideStatusGroup ? [] : [{ key: 'status', label: 'Status' }]),
    ...(hasProductionGroup ? [{ key: 'production', label: 'Production' }] : []),
  ];

  /* 🚨 A DISCLOSURE THAT OPENS AN EMPTY PANEL IS A CONTROL WITH NOTHING BEHIND
     IT — the class `pages/orderSortOptionsHaveHandlers.test.js` already
     ratchets. Hiding Status leaves a NON-ADMIN on the tabbed page with no
     groups at all (the Production group is admin-gated by the page), so the
     trigger itself must go rather than open on nothing. An admin keeps it,
     because Production survives. Pinned in BOTH directions by §9.4 and §9.5 —
     neither case alone distinguishes "correctly hidden" from "deleted". */
  const hasAnyFilterGroup = groups.length > 0;

  /* 🚨 **AND IF THERE IS NOTHING LEFT, RENDER NOTHING — NOT AN EMPTY GROUP.**
     A non-admin on Department Orders hides the view toggle AND the status group,
     which takes the `Filters ▾` trigger with it (above), so every child of this
     component is gone. `ui/Toolbar`'s shell is `gap-x-4` — the WIDE
     between-group gap — so an empty `TOOLBAR_GROUP` still consumes 16px and
     shifts everything after it: a visible hole in a bar the owner has twice
     complained about, produced by a component with nothing to say. This is
     §9.5's rule one level up (a disclosure that opens an empty panel is a control
     with nothing behind it).
     🪤 `children` counts — it is the page-owned slot, so a caller that hides
     both controls and passes a control of its own must still get it. The test is
     "have I anything to render", never "which props am I holding".
     ⚠️ jsdom lays nothing out, so the 16px is reasoning about the class rather
     than a measurement; §10.5 pins only that no node is emitted. */
  const rendersNothing = hideViewToggle && !hasAnyFilterGroup && !children;

  /* 🔑 THE ACTIVE CATEGORY IS HELD HERE, NOT ON THE PAGE — and that is the one
     place this differs from `GagFiltersPopover`, correctly. `ui/Dropdown`
     unmounts its PANEL when closed, which is why the Breakdown lifts this into
     the popover's parent; but THIS component renders the Dropdown, so it IS that
     parent and stays mounted on the toolbar throughout. Lifting it onto the page
     would add a prop to two pages to solve a problem neither has. */
  const [activeGroup, setActiveGroup] = useState('status');

  /* Enabled ⟺ pressing it would change something, and it must agree with the
     trigger badge — the `activeFilterCount` rule. The badge counts hidden
     statuses only, so the production filter is OR'd in explicitly rather than
     folded into `hiddenCount`, which several other things read. */
  const canClearFilters =
    hiddenCount > 0 || filterMode !== 'all' || (hasProductionGroup && productionFilter !== 'all')
    || (!hideStatusGroup && !showCompleted);

  if (rendersNothing) return null;

  return (
    <div className={TOOLBAR_GROUP} data-testid={wrapperTestId}>
      {/* One segmented unit, two segments. `role="group"` rather than
          `radiogroup`: the segments stay `aria-pressed` toggle buttons, which is
          what they already were and what every existing assertion reads — the
          change is geometry, not semantics.
          🪤 The TRACK is inside the gate, not just its buttons: an empty
          segmented recess is a control-shaped hole, which reads worse than no
          control at all. */}
      {!hideViewToggle && (
      <div className={TOOLBAR_SEGMENT_TRACK} role="group" aria-label="Order view">
        <button
          type="button"
          aria-pressed={allActive}
          /* 🪤 The select-all call is OPTIONAL: the tabbed page has no status
             set to re-select and passes no handler, and a bare call would throw
             on the commonest click on this bar. */
          onClick={() => { onFilterModeChange('all'); if (onSelectAllStatuses) onSelectAllStatuses(); }}
          data-testid={`${testIdPrefix}-all`}
          className={`${TOOLBAR_SEGMENT} ${allActive ? VIEW_SEG_ALL_ON : TOOLBAR_SEGMENT_OFF}`}
        >
          All ({allCount})
        </button>
        <button
          type="button"
          aria-pressed={filterMode === 'actionable'}
          onClick={() => onFilterModeChange('actionable')}
          data-testid={`${testIdPrefix}-actionable`}
          className={`${TOOLBAR_SEGMENT} ${filterMode === 'actionable' ? VIEW_SEG_ACTIONABLE_ON : TOOLBAR_SEGMENT_OFF}`}
        >
          Actionable ({actionableCount})
        </button>
      </div>
      )}

      {/* Filters ▾ — MASTER–DETAIL since 2026-08-10, breakdown-style. The
          trigger's "(N)" badge and every open/close mechanic still come from
          ui/Dropdown; only the PANEL changed.

          🚨 THE TRIGGER STAYS HERE AND MUST NOT MOVE INTO THE LAYOUT COMPONENT.
          `constants/inputs.controlHeight.test.js` §3 asserts the POPOVER FILE
          itself imports `TOOLBAR_DISCLOSURE_TRIGGER` — the same reason
          `GagFiltersPopover.js` keeps its own trigger rather than handing it to
          `GagFiltersMasterDetail`.

          📏 The panel widens from `w-[18rem]` to the Breakdown's
          `w-[20rem] sm:w-[28rem]` ONLY when there is a rail to fit — a 9rem rail
          beside a pane needs the width, and a single-group panel does not and
          would overhang a narrow screen sooner for nothing. */}
      {/* 🚨 MOBILE P1 FIX (2026-08-17) — `align="right"`, was `"left"`.
          This trigger sits at the RIGHT-HAND end of the toolbar's first
          group (segmented All/Actionable, then Filters ▾), so a
          left-anchored panel (`ui/Dropdown`'s default) ran its LEFT edge
          off the trigger's left edge and its 18rem/20rem/28rem width
          straight past the right edge of a 375px viewport — measured:
          `document.body.scrollWidth` 375 → 536px (161px of real PAGE-BODY
          horizontal scroll, not merely scroll inside the popover).
          🔑 Fixed HERE, not by flipping `ui/Dropdown`'s own default —
          that primitive is shared by triggers that do NOT sit at a
          toolbar's right edge (`Sort ▾` on this same bar, `Group ▾` /
          `Filters ▾` / `Views ▾` on the Breakdown), and its docblock's own
          rule for props ("every existing caller renders byte-for-byte
          what it rendered before") applies with equal force to the
          shared DEFAULT — changing it would move every OTHER caller's
          panel to anchor from the wrong edge to fix a problem specific to
          this one caller's position on its bar. See
          `client/src/components/OrderFilterBar.test.js` §8 for the
          structural pin (jsdom cannot see the viewport-overflow claim
          itself — that is a browser check). */}
      {hasAnyFilterGroup && (
      <Dropdown
        align="right"
        count={hiddenCount}
        haspopup="dialog"
        panelClassName={groups.length > 1 ? 'w-[20rem] sm:w-[28rem] p-3' : 'w-[18rem] p-3'}
        title={groups.length > 1 ? 'Filter the list' : 'Filter by status'}
        ariaLabel="Filters"
        label="Filters"
        triggerClassName={TOOLBAR_DISCLOSURE_TRIGGER}
        triggerTestId={`${testIdPrefix}-popover`}
      >
        <FiltersMasterDetail
          groups={groups}
          active={activeGroup}
          onSelect={setActiveGroup}
          idPrefix={`${testIdPrefix}-group`}
          railLabel="Filter categories"
          groupCount={(key) => orderFilterGroupCount({ statuses, selectedStatuses, productionFilter }, key)}
          countLabel={orderFilterCountLabel}
          canClear={canClearFilters}
          /* 🔑 ONE reset, expressed as a PATCH through the page's own
             `applyOrderFilterPatch` — the SAME function the chip row's "Clear
             all" runs, so the two controls can never come to mean two things.
             🪤 The order reset is `selectedStatuses: [...statuses]` — every
             status ON — which is the OPPOSITE of the Breakdown's `statuses: []`.
             Copying that one here would hide every order on the page. */
          onClearFilters={() => {
            if (onSelectAllStatuses) onSelectAllStatuses();
            onFilterModeChange('all');
            if (hasProductionGroup) onProductionFilterChange('all');
            if (onShowCompletedChange) onShowCompletedChange(true);
          }}
          renderGroup={(key) => (
            <OrderFilterGroupBody
              group={key}
              statuses={statuses}
              selectedStatuses={selectedStatuses}
              onToggleStatus={onToggleStatus}
              statusCounts={statusCounts}
              productionOptions={productionOptions}
              productionFilter={productionFilter}
              onProductionFilterChange={onProductionFilterChange}
              /* 🔻 The Completed pill lives INSIDE the status group, so it
                 leaves with it — on the tabbed page completion is the `Completed`
                 TAB. The prop is still threaded so the two paths stay one. */
              showCompleted={showCompleted}
              onShowCompletedChange={onShowCompletedChange}
              testIdPrefix={testIdPrefix}
            />
          )}
        />
      </Dropdown>
      )}

      {/* Page-owned controls (layout toggle, page size) — a SLOT, never
          internal JSX. See the docblock. */}
      {children}
    </div>
  );
}
